<!--
TODO:
- disabled no deberia poner en disabled el button, no es bueno para la accesibildad. Pero deberia evitarse que el boton mande eventos
- mejorar estado loading
- responsive: ancho completo en sm?
- useThrottle para el click, asi no toma doble clicks
-->

<template>
  <button
    ref="simpleButtonRef"
    type="button"
    :class="buttonClass"
    :disabled="disabled"
    :aria-disabled="disabled"
    @click="onClick"
  >
    <span v-if="!!$slots.leading && !loading" aria-hidden="true" :class="leadingIconClass">
      <slot name="leading"></slot>
    </span>
    <LoadingSpinner v-if="!!$slots.leading && loading" :class="leadingIconClass" aria-hidden="true" />
    <slot>{{ modelValue }}</slot>

    <span v-if="!!$slots.trailing && !loading" aria-hidden="true" :class="trailingIconClass">
      <slot name="trailing"></slot>
    </span>
    <LoadingSpinner v-if="!!$slots.trailing && loading" :class="trailingIconClass" aria-hidden="true" />
    <!-- <div v-if="loading" class="absolute left-0 right-0 bottom-0 h-1 bg-danger-90 animate-pulse" /> -->
  </button>
</template>

<script lang="ts" setup>
import { ref, computed, watchEffect } from "vue";

// Components
import LoadingSpinner from "./LoadingSpinner.vue";

// Composables
import { useTooltip } from "@composables/tooltips";

// Types
import { TippyOptions } from "vue-tippy";

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    disabled?: boolean;
    loading?: boolean;
    tooltip?: string;
    showTooltip?: boolean;
    selected?: boolean;
    tooltipProps?: TippyOptions;
    size?: "min" | "small" | "base";
    theme?:
      | "primary"
      | "primary-light"
      | "primary-clean"
      | "primary-clean-2"
      | "secondary"
      | "secondary-light"
      | "secondary-text"
      | "white"
      | "yellow"
      | "yellow-clean"
      | "danger"
      | "danger-alter"
      | "header-primary"
      | "header-accent"
      | "green-lime"
      | "header-sky";
  }>(),
  {
    modelValue: undefined,
    disabled: false,
    loading: false,
    tooltip: undefined,
    showTooltip: true,
    selected: false,
    tooltipProps: undefined,
    size: "base",
    theme: "primary",
  },
);

const emit = defineEmits<{
  click: [MouseEvent];
}>();

const slots = defineSlots<{
  leading?: () => any;
  default?: () => any;
  trailing?: () => any;
}>();

const onClick = (event: MouseEvent) => {
  if (props.loading) return;

  emit("click", event);
};

const simpleButtonRef = ref();
const tooltip = useTooltip(
  simpleButtonRef,
  computed(() => props.tooltip),
  {
    placement: "bottom",
    popperOptions: {
      modifiers: [
        {
          name: "preventOverflow",
          options: {
            altAxis: true,
            tether: false,
            boundary: "clippingParents",
          },
        },
        {
          name: "flip",
          options: {
            allowedAutoPlacements: ["bottom", "top", "bottom-end", "top-end"],
            fallbackPlacements: ["bottom", "top", "bottom-end", "top-end"],
            altBoundary: true,
          },
        },
      ],
    },
    ...props.tooltipProps,
  },
);
watchEffect(() => {
  if (props.showTooltip) {
    tooltip.enable();
  } else {
    tooltip.disable();
  }
});

const buttonClass = computed(() => {
  const classes: Array<string> = [];
  classes.push(
    "relative overflow-hidden inline-flex items-center justify-center border rounded-md font-medium focus:outline-none",
  );

  if (props.selected) {
    classes.push("outline-none");
  }

  if (props.size === "min") {
    classes.push("text-base sm:text-sm leading-4");
  } else if (props.size === "small") {
    classes.push("px-3 py-1.5 text-base sm:text-sm leading-4");
  } else if (props.size === "base") {
    classes.push("px-4 py-2 text-base sm:text-sm");
  }

  if (props.theme === "primary" || props.theme === "secondary") {
    classes.push("border-transparent shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-primary-60");
    if (props.selected) {
      classes.push("ring-2 ring-offset-2 ring-primary-60");
    }
    if (!props.disabled) {
      classes.push("text-neutral-100 bg-primary-60 hover:bg-primary-40");
    } else {
      classes.push("text-neutral-100 bg-neutral-90");
    }
  } else if (props.theme === "primary-light") {
    classes.push("border-transparent shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-primary-80");
    if (props.selected) {
      classes.push("ring-2 ring-offset-2 ring-primary-80");
    }
    if (!props.disabled) {
      classes.push("text-primary-60 bg-primary-99 hover:bg-primary-95");
    } else {
      classes.push("text-neutral-60 bg-neutral-99");
    }
  } else if (props.theme === "primary-clean") {
    classes.push("border-transparent focus:ring-2 focus:ring-offset-2 focus:ring-primary-60");
    if (props.selected) {
      classes.push("ring-2 ring-offset-2 ring-primary-60");
    }
    if (!props.disabled) {
      if (props.size !== "min") {
        classes.push("hover:bg-primary-99");
      }
      classes.push("text-primary-60  hover:text-primary-40");
    } else {
      classes.push("text-neutral-100");
    }
  } else if (props.theme === "primary-clean-2") {
    classes.push("border-transparent focus:ring-2 focus:ring-offset-2 focus:ring-primary-60");
    if (props.selected) {
      classes.push("ring-2 ring-offset-2 ring-primary-60");
    }
    if (!props.disabled) {
      if (props.size !== "min") {
        classes.push("bg-primary-95 hover:bg-primary-90");
      }
      classes.push("text-primary-20  hover:text-primary-10");
    } else {
      classes.push("text-neutral-100");
    }
  }
  // else if (props.theme === "secondary") {
  //   classes.push("border-transparent focus:ring-2 focus:ring-offset-2 focus:ring-primary-60");
  //   if (props.selected) {
  //     classes.push("ring-2 ring-offset-2 ring-primary-60");
  //   }
  //   if (!props.disabled) {
  //     classes.push("text-primary-20 bg-primary-90 hover:bg-primary-80");
  //   } else {
  //     classes.push("text-neutral-80 bg-neutral-99");
  //   }
  // }
  else if (props.theme === "secondary-light") {
    classes.push("border-transparent focus:ring-2 focus:ring-offset-2 focus:ring-primary-80");
    if (props.selected) {
      classes.push("ring-2 ring-offset-2 ring-primary-80");
    }
    if (!props.disabled) {
      classes.push("text-primary-40 bg-primary-95 hover:bg-primary-90");
    } else {
      classes.push("text-neutral-80 bg-neutral-99");
    }
  } else if (props.theme === "secondary-text") {
    classes.push("border-transparent focus:ring-2 focus:ring-offset-2 focus:ring-primary-60");
    if (props.selected) {
      classes.push("ring-2 ring-offset-2 ring-primary-60");
    }
    if (!props.disabled) {
      classes.push("text-primary-20");
    } else {
      classes.push("text-neutral-80");
    }
  } else if (props.theme === "white") {
    classes.push("shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-primary-60");
    if (props.selected) {
      classes.push("ring-2 ring-offset-2 ring-primary-60");
    }
    if (!props.disabled) {
      classes.push("border-neutral-90 text-neutral-0 bg-neutral-99 hover:bg-neutral-95");
    } else {
      classes.push("border-neutral-99 text-neutral-40 bg-neutral-99");
    }
  } else if (props.theme === "danger") {
    classes.push("border-transparent shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-danger-60");
    if (props.selected) {
      classes.push("ring-2 ring-offset-2 ring-danger-60");
    }
    if (!props.disabled) {
      classes.push("text-neutral-100 bg-danger-60 hover:bg-danger-40");
    } else {
      classes.push("text-neutral-100 bg-neutral-95");
    }
  } else if (props.theme === "danger-alter") {
    classes.push("border-transparent shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-danger-60");
    if (!props.disabled) {
      classes.push("text-danger-60 bg-danger-99 hover:bg-danger-95");
    } else {
      classes.push("text-neutral-100 bg-neutral-90");
    }
  } else if (props.theme === "yellow") {
    classes.push("border-transparent shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-warning-80");
    if (props.selected) {
      classes.push("ring-2 ring-offset-2 ring-warning-80");
    }
    if (!props.disabled) {
      classes.push("text-neutral-100 bg-warning-60 hover:bg-warning-40");
    } else {
      classes.push("text-neutral-100 bg-neutral-90");
    }
  } else if (props.theme === "yellow-clean") {
    classes.push("border-transparent focus:ring-2 focus:ring-offset-2 focus:ring-warning-40");
    if (props.selected) {
      classes.push("ring-2 ring-offset-2 ring-warning-40");
    }
    if (!props.disabled) {
      classes.push("text-warning-20 hover:bg-warning-95");
    } else {
      classes.push("text-neutral-100");
    }
  } else if (props.theme === "header-primary") {
    classes.push(
      "border-neutral-70 shadow-sm focus:ring-2 focus:ring-offset-neutral-60 focus:outline-none focus:ring-offset-2 focus:ring-neutral-70",
    );
    if (props.selected) {
      classes.push("ring-2 ring-offset-neutral-60 outline-none ring-offset-2 ring-neutral-70");
    }
    if (!props.disabled) {
      classes.push("text-neutral-99 bg-neutral-60 hover:border-neutral-70 hover:bg-neutral-40");
    } else {
      classes.push("text-neutral-80 bg-neutral-70");
    }
  } else if (props.theme === "header-accent") {
    classes.push(
      "border-transparent shadow-sm focus:ring-offset-neutral-60 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-warning-60",
    );
    if (props.selected) {
      classes.push("ring-offset-neutral-60 outline-none ring-2 ring-offset-2 ring-warning-60");
    }
    if (!props.disabled) {
      classes.push("text-neutral-40 bg-warning-60 hover:bg-warning-40");
    } else {
      classes.push("text-neutral-100 bg-neutral-90");
    }
  } else if (props.theme === "green-lime") {
    classes.push(
      "border-transparent shadow-sm focus:ring-offset-neutral-60 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lime-500",
    );
    if (props.selected) {
      classes.push("ring-offset-neutral-60 outline-none ring-2 ring-offset-2 ring-lime-500");
    }
    if (!props.disabled) {
      classes.push("text-neutral-40 bg-lime-300 hover:bg-lime-400");
    } else {
      classes.push("text-neutral-80 bg-neutral-70");
    }
  } else if (props.theme === "header-sky") {
    classes.push(
      "border-transparent shadow-sm focus:ring-offset-neutral-60 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-80",
    );
    if (props.selected) {
      classes.push("ring-offset-neutral-60 outline-none ring-2 ring-offset-2 ring-primary-80");
    }
    if (!props.disabled) {
      classes.push("text-neutral-40 bg-primary-80 hover:bg-primary-60");
    } else {
      classes.push("text-neutral-80 bg-neutral-70");
    }
  }

  if (props.disabled) {
    classes.push("cursor-default");
  }

  return classes.join(" ");
});

const leadingIconClass = computed(() => {
  const classes: Array<string> = [];
  if (props.size === "small") {
    classes.push("h-5 w-5");
    if (slots.default || props.modelValue) {
      classes.push("-ml-0.5 mr-2");
    }
  } else {
    classes.push("h-5 w-5");
    if (slots.default || props.modelValue) {
      classes.push("-ml-1 mr-2");
    }
  }
  if (props.theme === "white" && !props.disabled) classes.push("text-neutral-0");

  return classes;
});

const trailingIconClass = computed(() => {
  const classes: Array<string> = [];
  if (props.size === "small") {
    classes.push("h-5 w-5");
    if (slots.default || props.modelValue) {
      classes.push("ml-2 -mr-0.5");
    }
  } else {
    classes.push("h-5 w-5");
    if (slots.default || props.modelValue) {
      classes.push("ml-2 -mr-1");
    }
  }
  if (props.theme === "white" && !props.disabled) classes.push("text-neutral-0");

  return classes;
});
</script>
